import { Component, Mixins } from 'vue-property-decorator';
import { userStore } from '@/store';
import { AthleteProfileModel } from '../models/athlete/AthleteProfileModel';
import { AthleteProfileMixin } from '@/mixins';
import { CustomerCountry } from '@/../types';
import { BAEventModel, BAPurchaseModel } from '@/models';

/**
 * Provides computed properties: 
 * - TheAthleteId
 * - MyAthleteProfile
 */
@Component({
	async beforeRouteEnter(_, __, next): Promise<void>{
		if (!userStore.athleteProfilesInitialized && !userStore.loadAthleteProfilesLoading){
			await this.loadMyAthleteProfiles();
		}
		next();
	},
})
export class MyAthleteMixin extends Mixins(AthleteProfileMixin) {
	get IsAthleteLoading(): boolean {
		return this.IsLoadingAthleteProfile;
	}

	get CurrentAthleteId(): string | undefined {
		return userStore.athleteId ?? this.athleteId;
	}
	get TheAthleteId(): string{
		if(this.AthleteAppActive){
			return this.CurrentAthleteId;
		}
		return this.athleteId;
	}
	get TheAthleteProfile(): AthleteProfileModel | null {
		return this.athleteProfile;
	}
	
	async loadMyAthleteProfiles(): Promise<void> {
		await userStore.loadAthleteProfiles();
	}
	get AthletesReady(): boolean{
		return userStore.athleteProfilesInitialized && !userStore.loadAthleteProfilesLoading && !userStore.loading;
	}
	get MyAthleteProfile(): AthleteProfileModel {
		if( !!this.TheAthleteProfile ) return this.TheAthleteProfile;
		return userStore?.athlete;
	}
	get MyAthleteFullName(): string {
		if( !userStore ) return '-';
		return userStore.FullName;
	}
	get MyAthleteShareUrl(): string {
		return 'https://' + this.PublicAppUrl + this.MyAthleteProfile?.PublicSharingUrl;
	}

	updatingAthlete: boolean = false;
	updatingTeams: boolean = false;
	get UpdatingAthlete(): boolean {
		return this.updatingAthlete || this.updatingTeams;
	}

	get HasAthleteProfile(): boolean {
		return !!this.TheAthleteProfile;
	}
	get HasAthleteEvents(): boolean {
		if( !this.HasAthleteProfile ) return false;
		if( !this.TheAthleteProfile.events ) return false;
		return this.TheAthleteProfile.events.length > 0;
	}
	get AthleteEvents(): Array<BAEventModel> {
		if( !this.HasAthleteEvents ) return [];
		return this.TheAthleteProfile.events;
	}
	get HasAthleteAssociatedEvents(): boolean {
		if( !this.HasAthleteEvents ) return false;
		return this.TheAthleteProfile.associatedEvents.length > 0;
	}
	get AthleteAssociatedEvents(): Array<BAEventModel> {
		if( !this.HasAthleteAssociatedEvents ) return [];
		return this.TheAthleteProfile.associatedEvents;
	}
	get HasFutureAssociatedEvents(): boolean {
		if( !this.HasAthleteAssociatedEvents ) return false;
		const futureEvent = this.AthleteAssociatedEvents.find(e => e.IsFuture);
		return !!futureEvent;
	}
	get AthleteFutureAssociatedEvents(): Array<BAEventModel> {
		if( !this.HasFutureAssociatedEvents ) return [];
		return this.AthleteAssociatedEvents.filter(e => e.IsFuture);
	}
	get HasAthletePurchases(): boolean {
		if( !this.HasAthleteProfile ) return false;
		if( !this.TheAthleteProfile.purchases ) return false;
		return this.TheAthleteProfile.purchases.length > 0;
	}
	get AthletePurchases(): Array<BAPurchaseModel> {
		if( !this.HasAthletePurchases ) return [];
		return this.TheAthleteProfile.purchases;
	}
	get HasAthleteIncompletePurchases(): boolean {
		if( !this.HasAthletePurchases ) return false;
		return this.AthletePurchases.filter(p => !p.completed).length > 0;
	}
	get AthleteIncompletePurchases(): Array<BAPurchaseModel> {
		if( !this.AthleteAppActive ) return [];
		if( !this.HasAthleteIncompletePurchases ) return [];
		return this.AthletePurchases.filter(p => !p.completed);
	}
	AthleteEvent(eventId: string): BAEventModel {
		if( !this.HasAthleteEvents ) return undefined;
		return this.AthleteEvents.find(e => e.id === eventId);
	}
    IsEventPurchaseStarted(event: BAEventModel): boolean {
        if( !this.HasAthletePurchases) return false;
        const purchaseOfEvent = this.TheAthleteProfile.purchases.find(p => p.productId === event.id);
        return purchaseOfEvent?.requested;
    }
}
